import apiClient from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type { EntityFormResolutionResult, EntityFormType, EntityFormActionType, FormContextType } from '../types/entityFormResolution';

/**
 * Service for the unified entity form resolution API.
 * Replaces per-entity form discovery (hospitalService.getAvailableForms, etc.)
 * with a single resolution endpoint.
 */
export const entityFormResolutionService = {
  /**
   * Resolves the correct form for an entity type within a program.
   * Resolution chain: runtime mapping → workflow mapping → direct ProgramForm fallback.
   * Action type defaults to CREATE when not specified.
   * Version is optional; defaults to latest PUBLISHED.
   */
  async resolveEntityForm(
    programId: string,
    entityType: EntityFormType,
    actionType?: EntityFormActionType,
    version?: number,
    workflowStepId?: string,
    contextType?: FormContextType
  ): Promise<ApiResponse<EntityFormResolutionResult>> {
    const params = new URLSearchParams({ entityType });
    if (actionType) {
      params.append('action', actionType);
    }
    if (version !== undefined && version !== null) {
      params.append('version', version.toString());
    }
    if (workflowStepId) {
      params.append('workflowStepId', workflowStepId);
    }
    if (contextType) {
      params.append('contextType', contextType);
    }
    const response = await apiClient.get<ApiResponse<EntityFormResolutionResult>>(
      `/programs/${programId}/entity-forms/resolve?${params.toString()}`
    );
    return response.data;
  },
};
